<template>
  <div class="tp-grid-wrapper">
    <!--
      ズームは実際に縮小して表示できる件数を増やすためのもの。
      幅を狭めると逆に折り返しが増えるだけなので、CSSのzoomで全体を縮小する。
    -->
    <div class="tp-grid" :style="{ zoom: String(zoom / 100) }">
      <div v-for="(row, rowIndex) in layout.rows" :key="rowIndex" class="tp-grid__row">
        <div
          v-for="(cell, cellIndex) in row.cells"
          :key="`${rowIndex}-${cellIndex}`"
          class="tp-grid__col"
          :style="{ width: `${widthPercents(row.widths)[cellIndex]}%` }"
        >
          <teaching-plan-cell
            :ref="cellRefName(cell)"
            :cell="cell"
            :value="valueOf(cell)"
            :reference="referenceOf(cell)"
            :editable="editable"
            :selected="selectedItemKey !== null && selectedItemKey === cell.itemKey"
            :comment-count="commentCountOf(cell)"
            :transfer="transferOf(cell)"
            @select="$emit('select', $event)"
            @change="$emit('change', $event)"
            @jump-source="$emit('jump-source', $event)"
            @transfer-cell="$emit('transfer-cell', $event)"
          ></teaching-plan-cell>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import {
  NurseryTeachingPlanLayout,
  NurseryTeachingPlanLayoutCell,
  NurseryTeachingPlanResolvedReference,
  NurseryTeachingPlanValue,
} from "chaild-api/lib";
import TeachingPlanCell, {
  TeachingPlanCellTransfer,
} from "@/components/teaching-plan/TeachingPlanCell.vue";
import TeachingPlanUtil from "@/utils/TeachingPlanUtil";

/**
 * レイアウトJSONを行×列で描画するグリッド。
 * 列幅は widths の比率をそのまま百分率に直す (Excel出力と同じ見え方にする)。
 */
export default defineComponent({
  name: "TeachingPlanGrid",

  components: { TeachingPlanCell },

  props: {
    layout: {
      type: Object as PropType<NurseryTeachingPlanLayout>,
      required: true,
    },
    values: {
      type: Object as PropType<{ [itemKey: string]: NurseryTeachingPlanValue }>,
      default: () => ({}),
    },
    resolvedReferences: {
      type: Array as PropType<NurseryTeachingPlanResolvedReference[]>,
      default: () => [],
    },
    unresolvedCommentCounts: {
      type: Object as PropType<{ [itemKey: string]: number }>,
      default: () => ({}),
    },
    /** carryルールの転記先セル (itemKey -> 転記元ラベルと引き込み可否) */
    transferCells: {
      type: Object as PropType<{ [itemKey: string]: TeachingPlanCellTransfer }>,
      default: () => ({}),
    },
    editable: {
      type: Boolean,
      default: false,
    },
    selectedItemKey: {
      type: [String, null] as PropType<string | null>,
      default: null,
    },
    /** 表示倍率 (%) */
    zoom: {
      type: Number,
      default: 100,
    },
  },

  emits: ["select", "change", "jump-source", "transfer-cell"],

  methods: {
    widthPercents(widths: number[]): number[] {
      return TeachingPlanUtil.widthPercents(widths);
    },
    valueOf(cell: NurseryTeachingPlanLayoutCell): string | null {
      if (!cell.itemKey) {
        return null;
      }
      const entry = this.values[cell.itemKey];
      return entry ? entry.value : null;
    },
    referenceOf(
      cell: NurseryTeachingPlanLayoutCell
    ): NurseryTeachingPlanResolvedReference | null {
      if (!cell.itemKey || cell.type !== "reference") {
        return null;
      }
      return this.resolvedReferences.find((r) => r.itemKey === cell.itemKey) || null;
    },
    commentCountOf(cell: NurseryTeachingPlanLayoutCell): number {
      if (!cell.itemKey) {
        return 0;
      }
      return this.unresolvedCommentCounts[cell.itemKey] || 0;
    },
    transferOf(cell: NurseryTeachingPlanLayoutCell): TeachingPlanCellTransfer | null {
      if (!cell.itemKey) {
        return null;
      }
      return this.transferCells[cell.itemKey] || null;
    },
    cellRefName(cell: NurseryTeachingPlanLayoutCell): string {
      return cell.itemKey ? `cell-${cell.itemKey}` : "cell-label";
    },
    /** 入力支援パネルからの本文挿入をセルへ委譲する */
    insertTextToCell(itemKey: string, text: string) {
      const refs = this.$refs[`cell-${itemKey}`] as
        | InstanceType<typeof TeachingPlanCell>[]
        | undefined;
      const target = Array.isArray(refs) ? refs[0] : refs;
      if (target && typeof target.insertText === "function") {
        target.insertText(text);
      }
    },
    /** 指定セルまでスクロールする (未解決コメントのジャンプ用) */
    scrollToCell(itemKey: string) {
      const refs = this.$refs[`cell-${itemKey}`] as
        | InstanceType<typeof TeachingPlanCell>[]
        | undefined;
      const target = Array.isArray(refs) ? refs[0] : refs;
      if (target && target.$el && typeof target.$el.scrollIntoView === "function") {
        target.$el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    },
  },
});
</script>

<style scoped>
.tp-grid-wrapper {
  overflow-x: auto;
  background-color: #fafafa;
  padding: 8px;
}
.tp-grid {
  border-top: 1px solid #e0e0e0;
  border-left: 1px solid #e0e0e0;
  background-color: #ffffff;
  min-width: 720px;
}
.tp-grid__row {
  display: flex;
  align-items: stretch;
}
.tp-grid__col {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.tp-grid__col > * {
  flex: 1;
}
</style>
